import { Button } from "@/components/ui/button";
import { ApiError, api } from "@/lib/api";
import type { Pixels } from "@/lib/document-enhance";
import { photoBlob, prepareDocumentPhoto, turnClockwise } from "@/lib/document-photo";
import { type QueuedScan, enqueueScan, flushQueue, listQueue } from "@/lib/scan-queue";
import { installedScannerVersion, isScannerApp, scannerUpdateAvailable } from "@/lib/scanner-app";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Download, RotateCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `/scan`: Netlion Scanner, the Android app's only screen. Photograph,
 * send, see what was sent; nothing else of Hub. The photo goes into the
 * phone's queue first, so it can be taken without a signal.
 *
 * The camera opens through `<input capture>`: the native camera app does
 * focus, flash and HDR better than a canvas would.
 */

export const Route = createFileRoute("/scan")({
  loader: async () => {
    try {
      await api.get("/api/me");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: "/login" });
      // Offline: the queue works without the server, so the page opens anyway.
    }
  },
  component: ScanPage,
});

type RecentScan = {
  id: string;
  status: "pending" | "extracted" | "failed" | "filed" | "discarded";
  note: string | null;
  capturedAt: string | null;
  createdAt: string;
  supplierName: string | null;
  total: string | null;
  currency: string | null;
};

const STATUS: Record<RecentScan["status"], string> = {
  pending: "Being read",
  extracted: "Read, waiting for the accountant",
  failed: "Couldn't be read, the accountant will fill it in",
  filed: "In the books",
  discarded: "Discarded",
};

type Shot = {
  original: Pixels;
  enhanced: Pixels;
  showOriginal: boolean;
  url: string;
  blob: Blob;
};

/** The download URL when the installed app is older than the APK Hub serves. */
function useScannerUpdate(): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const search = window.location.search;
    if (!isScannerApp(search)) return;
    const installed = installedScannerVersion(search);
    fetch("/app/netlion-scanner.json")
      .then((res) =>
        res.ok ? (res.json() as Promise<{ version: number | null; url: string }>) : null,
      )
      .then((latest) => {
        if (latest && scannerUpdateAvailable(installed, latest.version)) setUrl(latest.url);
      })
      .catch(() => {});
  }, []);
  return url;
}

function ScanPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [shot, setShot] = useState<Shot | null>(null);
  const [queue, setQueue] = useState<QueuedScan[]>([]);
  const [recent, setRecent] = useState<RecentScan[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const update = useScannerUpdate();

  const refresh = useCallback(async () => {
    setQueue(await listQueue().catch(() => []));
    try {
      const { scans } = await api.get<{ scans: RecentScan[] }>("/api/accounting/inbox/mine");
      setRecent(scans);
    } catch {
      // Offline or not allowed: keep whatever list was shown.
    }
  }, []);

  const send = useCallback(async () => {
    setBusy(true);
    try {
      const result = await flushQueue();
      if (result.sent > 0 && result.remaining === 0) {
        setStatus(`Sent (${result.sent}).`);
      } else if (result.remaining > 0) {
        setStatus(
          result.sent > 0
            ? `Sent ${result.sent}, ${result.remaining} waiting.`
            : `Not sent yet, ${result.remaining} waiting. They leave on their own once there is a signal.`,
        );
      }
    } finally {
      setBusy(false);
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();
    const goOnline = () => {
      setOnline(true);
      void send();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) void send();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refresh, send]);

  async function show(next: Omit<Shot, "url" | "blob">) {
    const { blob, url } = await photoBlob(next.showOriginal ? next.original : next.enhanced);
    setShot((previous) => {
      if (previous) URL.revokeObjectURL(previous.url);
      return { ...next, blob, url };
    });
  }

  async function onPhotoPicked(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setStatus(null);
    try {
      const photo = await prepareDocumentPhoto(file);
      await show({ original: photo.original, enhanced: photo.enhanced, showOriginal: false });
    } catch {
      setStatus("Couldn't process the photo. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function rotate() {
    if (!shot) return;
    setBusy(true);
    try {
      await show({
        original: turnClockwise(shot.original),
        enhanced: turnClockwise(shot.enhanced),
        showOriginal: shot.showOriginal,
      });
    } finally {
      setBusy(false);
    }
  }

  async function toggleOriginal() {
    if (!shot) return;
    setBusy(true);
    try {
      await show({ ...shot, showOriginal: !shot.showOriginal });
    } finally {
      setBusy(false);
    }
  }

  async function sendShot() {
    if (!shot) return;
    setBusy(true);
    try {
      await enqueueScan({ blob: shot.blob, note: "" });
      URL.revokeObjectURL(shot.url);
      setShot(null);
      setStatus("Saved. Sending…");
    } catch {
      setStatus("Couldn't save it on this phone.");
      setBusy(false);
      return;
    }
    await send();
  }

  function discard() {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
  }

  return (
    <div className="mx-auto min-h-screen w-full max-w-lg space-y-4 bg-background p-4">
      <header className="flex items-center gap-3">
        <img src="/apps/scanner.svg" alt="" className="h-9 w-9" />
        <h1 className="font-semibold text-xl">Netlion Scanner</h1>
      </header>

      {update && (
        <a href={update} className="flex items-center gap-3 rounded-md bg-primary/10 p-3 text-sm">
          <Download className="h-5 w-5 shrink-0" />
          <span className="flex-1">
            A new version of Netlion Scanner is ready. Tap to download it, then confirm the install.
          </span>
        </a>
      )}

      {!online && (
        <p className="rounded-md bg-amber-50 p-3 text-amber-900 text-sm dark:bg-amber-950/40 dark:text-amber-200">
          The phone is offline. Keep shooting, documents wait on the phone and leave on their own.
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPhotoPicked}
      />

      {shot ? (
        <div className="space-y-3">
          <img
            src={shot.url}
            alt="Photographed document"
            className="max-h-[60vh] w-full rounded-lg border bg-muted object-contain"
          />
          <div className="flex gap-2">
            <Button variant="outline" className="h-11 flex-1" disabled={busy} onClick={rotate}>
              <RotateCw className="mr-2 h-4 w-4" />
              Rotate
            </Button>
            <Button
              variant="outline"
              className="h-11 flex-1"
              disabled={busy}
              onClick={toggleOriginal}
            >
              {shot.showOriginal ? "Show cleaned up" : "Show original"}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button className="h-14 flex-1 text-base" disabled={busy} onClick={sendShot}>
              {busy ? "Working…" : "Send"}
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-1 text-base"
              disabled={busy}
              onClick={discard}
            >
              Retake
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className="h-24 w-full text-lg"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? "Processing…" : "Photograph a document"}
        </Button>
      )}

      {status && <p className="text-muted-foreground text-sm">{status}</p>}

      {queue.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border p-3">
          <span className="font-medium text-sm">Waiting to be sent: {queue.length}</span>
          <Button variant="outline" size="sm" disabled={busy} onClick={send}>
            Try again
          </Button>
        </div>
      )}

      {recent && recent.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-medium text-sm text-muted-foreground">Recently sent</h2>
          <ul className="divide-y rounded-lg border">
            {recent.map((scan) => (
              <li key={scan.id} className="flex items-center gap-3 p-2">
                <img
                  src={`/api/accounting/inbox/${scan.id}/photo`}
                  alt=""
                  loading="lazy"
                  className="h-14 w-11 shrink-0 rounded border bg-muted object-cover"
                />
                <div className="min-w-0 flex-1 text-sm">
                  <p className="truncate font-medium">
                    {scan.supplierName ?? new Date(scan.createdAt).toLocaleString("cs-CZ")}
                    {scan.total && ` · ${scan.total} ${scan.currency ?? ""}`}
                  </p>
                  <p className="truncate text-muted-foreground text-xs">
                    {scan.supplierName &&
                      `${new Date(scan.createdAt).toLocaleDateString("cs-CZ")} · `}
                    {STATUS[scan.status]}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
